<!-- @format -->
<template>
    <div class="company-files-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title || 'Add New File' }}</h3>
                <div class="widget-header-actions">
                    <div class="svg-container widget-close-button" @click="closeWidget">
                        <widget-close-svg />
                    </div>
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="step === 'AddNewFile'">
                    <div class="add-item-container">
                        <div class="add-item">
                            <div class="add-item-inner-container">
                                <div class="bg-wrapper">
                                    <div
                                        ref="dragDropArea"
                                        :class="{ 'dropping-files': draggedFileEnteredArea }"
                                        class="add-item-upload-section"
                                        @dragenter="dragEnter($event)"
                                        @dragleave="dragLeave($event)"
                                        @dragover="dragOver($event)"
                                        @drop="drop($event)"
                                    >
                                        <div class="file-upload">
                                            <input
                                                id="file-input"
                                                type="file"
                                                name="file"
                                                multiple
                                                class="btn blue-btn small-btn"
                                                @change="getFilesFromInput"
                                            />
                                        </div>
                                        <h3>{{ dragDropFilesText }}</h3>
                                        <div class="svg-container">
                                            <file-open-svg></file-open-svg>
                                        </div>
                                        <button
                                            :disabled="draggedFileEnteredArea"
                                            class="btn blue-btn select-files-button"
                                            @click.prevent="clickFileUploadInput()"
                                        >
                                            <span v-show="filesForUpload.length === 0">
                                                Choose File(s)
                                            </span>
                                            <span v-show="filesForUpload.length > 0">
                                                Upload More
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                <div class="upload-information-section">
                                    <p>Maximum upload file size: <strong>100 MB</strong></p>
                                </div>
                                <div
                                    v-show="filesForUpload.length > 0"
                                    class="upload-progress-section"
                                >
                                    <div
                                        v-for="file in filesForUpload"
                                        :key="file.id"
                                        class="file-progress"
                                    >
                                        <div class="uploaded-file-details">
                                            <div class="svg-container">
                                                <component
                                                    :is="fileTypeIcon(file.simplifiedFileType)"
                                                />
                                            </div>
                                            <p>{{ file.name }}</p>
                                        </div>
                                        <div class="uploaded-file-metrics">
                                            <div v-if="file.isFileTooLarge" class="file-too-large">
                                                <p>File is too large for upload!</p>
                                            </div>
                                            <div v-else class="progress-area">
                                                <p class="file-progress-text">
                                                    {{
                                                        file.percentDone === '100%'
                                                            ? 'Completed!'
                                                            : file.percentDone
                                                    }}
                                                </p>
                                                <div class="progress">
                                                    <div
                                                        class="progress-bar"
                                                        :class="{ complete: file.complete }"
                                                        role="progressbar"
                                                        :style="{ width: file.percentDone }"
                                                        aria-valuenow="0"
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="divider"><strong class="text">OR</strong></div>
                            <div class="upload-link-container">
                                <app-input v-model="link" label="Add a Link" />
                                <app-button
                                    :onClick="uploadLink"
                                    :disabled="isUploadingFiles"
                                    :loading="isUploadingLink"
                                    :class="{ 'loading-link': isUploadingLink }"
                                >
                                    Add
                                </app-button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="step == 'EditName'" class="edit-name">
                    <div class="preview-image-wrapper">
                        <div
                            v-if="enrichedLink.previewUrl"
                            :style="{
                                'background-image': 'url(\'' + enrichedLink.previewUrl + '\')',
                            }"
                            class="preview-image-container"
                        ></div>
                        <div v-else class="svg-container">
                            <weblink-files-svg></weblink-files-svg>
                        </div>
                    </div>
                    <app-input v-model="enrichedLink.name" label="File name" />
                </div>
                <div v-else-if="step === 'EditFileName'" class="edit-name">
                    <div v-if="steps[step].loading">
                        <loading-screen-svg />
                    </div>
                    <div v-else>
                        <div class="form-row">
                            <img
                                v-if="hasPreview"
                                class="preview-image"
                                :src="companyFile.previewImageUrl"
                                alt="preview image"
                            />
                            <app-file-icon v-else :type="companyFile.fileType" />
                        </div>
                        <div class="form-row">
                            <app-input v-model="companyFile.name" label="Name" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" :onClick="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <button
                        v-if="step == 'EditName'"
                        class="transparent-btn btn normal"
                        @click="goToAddNewFile"
                    >
                        Back
                    </button>
                    <app-button
                        :onClick="steps[step].action"
                        :loading="steps[step].loading"
                        :class="{ 'loading-link': steps[step].loading }"
                    >
                        {{ steps[step].buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const AppFileIcon = () => import('@/components/common/app-file-icon');
const BlankFileSvg = () => import('@/components/svg/blank-file-svg');
const ExcelFileSvg = () => import('@/components/svg/excel-file-svg');
const FileOpenSvg = () => import('@/components/svg/file-open-svg');
const ImageFileSvg = () => import('@/components/svg/image-file-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const PaperSvg = () => import('@/components/svg/doc-file-svg');
const PdfFileSvg = () => import('@/components/svg/pdf-file-svg');
const PlayButtonSvg = () => import('@/components/svg/play-button-svg');
const PresentationFileSvg = () => import('@/components/svg/presentation-file-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const WebLinkSvg = () => import('@/components/svg/weblink-file-svg');
const ZipFileSvg = () => import('@/components/svg/zip-file-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'company-files-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-file-icon': AppFileIcon,
        'file-open-svg': FileOpenSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'weblink-files-svg': WebLinkSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            clientId: this.options.clientId || null,
            agencyId: this.options.agencyId || null,
            companyId: this.options.companyId || null,
            fileId: this.options.fileId || null,
            companyFile: {
                previewImageUrl: null,
                name: null,
                fileType: null,
                fileExtension: null,
            },

            step: null,
            draggedFileEnteredArea: false,
            isPreviouslyUploaded: false,
            isUploadingFiles: false,
            link: null,
            isUploadingLink: false,
            enrichedLink: {
                previewUrl: null,
                name: null,
                url: null,
                mimeType: null,
                fileExtension: null,
                fileSizeInBytes: null,
                uploadedByUserId: null,
                clientId: null,
                agencyId: null,
            },

            dragDropFilesText: 'Drag Your Files Here Or',
            filesForUpload: [],

            steps: {
                AddNewFile: {
                    title: 'Add New File',
                    buttonText: 'Back to Company Details',
                    action: this.closeWidget,
                    loading: false,
                },

                EditName: {
                    title: 'Edit Name',
                    buttonText: 'Save',
                    action: this.postLink,
                    loading: false,
                },

                EditFileName: {
                    title: 'Edit Name',
                    buttonText: 'Save Changes',
                    action: this.saveChanges,
                    loading: false,
                },
            },

            noPreviewExtensions: ['ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx'],
        };
    },

    computed: {
        userId() {
            return this.$store.state.user.id;
        },

        hasPreview() {
            return !this.noPreviewExtensions.includes(this.companyFile.fileExtension);
        },
    },

    created() {
        if (this.fileId) {
            this.step = 'EditFileName';
            this.getFile();
        } else {
            this.step = 'AddNewFile';
        }
    },

    methods: {
        fileTypeIcon(fileType) {
            switch (fileType) {
                case 'document':
                    return PaperSvg;
                case 'video':
                    return PlayButtonSvg;
                case 'pdf':
                    return PdfFileSvg;
                case 'archive':
                    return ZipFileSvg;
                case 'excel':
                    return ExcelFileSvg;
                case 'image':
                    return ImageFileSvg;
                case 'presentation':
                    return PresentationFileSvg;
                default:
                    return BlankFileSvg;
            }
        },

        getFile() {
            return new Promise((resolve, reject) => {
                this.steps.EditFileName.loading = true;
                this.$api
                    .get(`/service/CompanyFiles?$filter=Id eq ${this.fileId}`)
                    .then((response) => {
                        const responseValue = response.data.value[0];
                        this.companyFile.fileType = (responseValue.FileType || '').toLowerCase();
                        this.companyFile.fileExtension = responseValue.FileExtension;
                        this.companyFile.previewImageUrl = responseValue.PreviewImageUrl;
                        this.companyFile.name = this.$helpers.getFilenameWithoutExtension(
                            responseValue.Name,
                            responseValue.FileExtension,
                        );
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your file.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.steps.EditFileName.loading = false;
                    });
            });
        },

        saveChanges() {
            const sanitizedName = this.$helpers.sanitizeFileName(this.companyFile.name);
            const name = this.companyFile.fileExtension
                ? `${sanitizedName}.${this.companyFile.fileExtension}`
                : sanitizedName;

            const changes = [
                {
                    name: 'Name',
                    value: name,
                },
                {
                    name: 'ClientId',
                    value: this.clientId,
                },
                {
                    name: 'AgencyId',
                    value: this.agencyId,
                },
            ];

            this.$api
                .patch(`/api/CompanyFiles/${this.fileId}`, changes)
                .then(() => {
                    EventBus.$emit('company-files:update');
                    this.$helpers.showSuccessNotification('Your file has been updated.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your file.',
                        { error },
                    );
                });
        },

        clearInput() {
            const fileInput = $('#file-input');
            fileInput.val(null);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        postLink() {
            const request = {
                Url: this.enrichedLink.url,
                Name: this.enrichedLink.name,
                MimeType: this.enrichedLink.mimeType,
                FileExtension: this.enrichedLink.fileExtension,
                FileType: this.enrichedLink.fileType,
                FileSizeInBytes: this.enrichedLink.fileSizeInBytes,
                PreviewImageUrl: this.enrichedLink.previewUrl,
                UploadedByUserId: this.userId,
                CompanyId: this.companyId,
                ClientId: this.clientId,
                AgencyId: this.agencyId,
            };

            this.steps.EditName.loading = true;

            this.$api
                .post('/api/CompanyFiles', request)
                .then((response) => {
                    this.sendSuccessNotification(response);

                    this.link = null;
                    this.steps.EditName.loading = false;
                    this.step = 'AddNewFile';
                    EventBus.$emit('company-files:update');
                })
                .catch((response) => {
                    this.steps.EditName.loading = false;
                    this.sendGeneralFailNotification(response);
                })
                .finally(() => {
                    this.closeWidget();
                });
        },

        uploadLink() {
            this.isUploadingLink = true;

            const request = {
                Links: [
                    {
                        Url: this.link,
                    },
                ],
            };

            this.$api
                .post('/api/Collaterals/Links/Enrich', request)
                .then((response) => {
                    this.hydrateEnrichedLinks(response.data.Links[0]);

                    this.isUploadingLink = false;
                    this.step = 'EditName';
                })
                .catch((response) => {
                    this.isUploadingLink = false;
                    this.sendGeneralFailNotification(response);
                });
        },

        hydrateEnrichedLinks(data) {
            this.enrichedLink.previewUrl = data.ImageUrl;
            this.enrichedLink.url = data.Url;
            this.enrichedLink.name = data.Name ? data.Name : data.Title;
            this.enrichedLink.title = data.Title;
            this.enrichedLink.mimeType = data.MimeType;
            this.enrichedLink.fileExtension = data.FileExtension;
            this.enrichedLink.fileType = data.FileType;
            this.enrichedLink.fileSizeInBytes = data.FileSizeInBytes;
            this.enrichedLink.description = data.Description;
            this.enrichedLink.domain = data.domain;
            this.enrichedLink.uploadedByUserId = this.userId;
            this.enrichedLink.companyId = this.companyId;
            this.enrichedLink.clientId = this.clientId;
            this.enrichedLink.agencyId = this.agencyId;
        },

        sendGeneralFailNotification(response) {
            let message = '';

            if (response.response.data.Message) {
                message = response.response.data.Message;
            } else {
                message = response.response.statusText;
            }

            this.$helpers.showFailNotification(message);
        },

        sendSuccessNotification() {
            const message = `Added successfully: <strong>${this.enrichedLink.name}</strong>`;
            this.$helpers.showSuccessNotification(message);
        },

        goToAddNewFile() {
            this.step = 'AddNewFile';
        },

        dragEnter(e) {
            e.preventDefault();
            this.draggedFileEnteredArea = true;
            this.dragDropFilesText = 'Drop Your Files';
        },

        dragLeave(e) {
            e.preventDefault();
            this.draggedFileEnteredArea = false;
            this.dragDropFilesText = 'Drag Your Files Here Or';
        },

        dragOver(e) {
            e.preventDefault();
        },

        drop(e) {
            e.preventDefault();
            this.draggedFileEnteredArea = false;
            const files = e.dataTransfer.files;
            this.iterateOverAndValidateFiles(files);
        },

        getFilesFromInput() {
            const files = $('#file-input')[0].files;
            this.iterateOverAndValidateFiles(files);
        },

        iterateOverAndValidateFiles(files) {
            for (const file of files) {
                const newFile = this.createFile(file);
                this.filesForUpload.push(newFile);

                if (file.size <= 100000000) {
                    this.addFileToFormData(file, newFile);
                } else {
                    newFile.isFileTooLarge = true;
                }
            }

            this.isPreviouslyUploaded = true;
        },

        createFile(file) {
            return {
                id: this.$helpers.createId('file'),
                name: this.$helpers.sanitizeFileName(file.name),
                extension: file.name.split('.').splice(-1, 1).join(),
                simplifiedFileType: this.$helpers.getDocumentTypeFromMimeType(file.type),
                percentDone: '0%',
                complete: false,
                isFileTooLarge: false,
            };
        },

        clickFileUploadInput() {
            const $fileInput = $('#file-input');

            if (this.isPreviouslyUploaded) {
                $fileInput.val(null);
            }

            $fileInput.click();
        },

        addFileToFormData(fileForSend, fileForDisplay) {
            this.isFileChosen = true;
            const formData = new FormData();

            formData.append('file', fileForSend, this.$helpers.sanitizeFileName(fileForSend.name));
            formData.append('companyId', this.companyId);
            formData.append('clientId', this.clientId);
            formData.append('agencyId', this.agencyId);

            this.sendUploadFileRequest(formData, fileForDisplay);
        },

        sendUploadFileRequest(formData, fileForDisplay) {
            let self = this;

            self.isUploadingFiles = false;
            this.steps.AddNewFile.loading = true;

            self.$api
                .ajax({
                    url: '/api/CompanyFiles/File',
                    method: 'POST',
                    data: formData,
                    contentType: false,
                    processData: false,
                    onUploadProgress: function (progressEvent) {
                        self.onUploadProgress(progressEvent, fileForDisplay);
                    },
                })
                .then(() => {
                    fileForDisplay.percentDone = '100%';
                    fileForDisplay.complete = true;

                    this.clearInput();
                    this.isUploadingFiles = false;
                    EventBus.$emit('company-files:update');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your company file',
                        { error },
                    );
                })
                .finally(() => {
                    self.isUploadingFiles = false;
                    this.steps.AddNewFile.loading = false;
                });
        },

        onUploadProgress(event, file) {
            let percent = (event.loaded / event.total) * 100;
            percent = Math.floor(percent);

            if (percent <= 100) {
                file.percentDone = percent + '%';
            }
        },
    },
};
</script>

<style scoped>
.company-files-widget {
    width: 100%;
}

.widget.company-files-widget .content-wrapper {
    padding: 0;
}

.company-files-widget .preview-image {
    display: block;
    margin: 0 auto;
    max-height: 300px;
    max-width: 100%;
}

.company-files-widget .widget-header-actions {
    display: flex;
}

.company-files-widget .action-container {
    display: flex;
}

.company-files-widget .action-container .btn {
    margin-left: 15px;
}

.company-files-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.company-files-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.company-files-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.company-files-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.company-files-widget .action-container .btn.white-btn {
    background: #ffffff;
    border: 2px solid #e9e9ef;
    color: #877d88;
}

.company-files-widget .action-container .btn.normal {
    border-radius: 20px;
    font-size: 14px;
    height: 40px;
    line-height: 17px;
    padding: 0px 25px;
}

.company-files-widget .action-container .btn.normal:hover {
    transition: background-color 250ms ease-in;
}

.company-files-widget .accent-color {
    color: #877d88;
}

.company-files-widget .loading-link {
    background-color: var(--color-fill-accent-skyblue-medium); /*mass replace */
    color: #fff;
}

.company-files-widget .add-item-container {
    width: 100%;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
}

.company-files-widget .add-item-container .add-item {
    padding: 25px;
    width: 100%;
    height: 100%;
}

.company-files-widget .add-item-inner-container {
    position: relative;
    z-index: 0;
    height: 100%;
    width: 100%;
}

.company-files-widget .add-item-upload-section {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    text-align: center;
    border-radius: 10px;
    border: 2px dashed #382153;
    width: 100%;
    height: 230px;
    margin-bottom: 10px;

    -webkit-box-shadow: inset 0px 2px 5px 1px rgba(0, 0, 0, 0.3);
    -moz-box-shadow: inset 0px 2px 5px 1px rgba(0, 0, 0, 0.3);
    box-shadow: inset 0px 2px 5px 1px rgba(0, 0, 0, 0.3);
}

.company-files-widget .add-item-upload-section.dropping-files {
    /* background-color: #f3f4f9; */
    background-color: rgba(24, 174, 255, 0.31);
    -webkit-box-shadow: none;
    box-shadow: none;
}

.company-files-widget .upload-information-section,
.company-files-widget .upload-progress-section {
    margin-bottom: 15px;
}

.company-files-widget .file-progress {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    position: relative;
}

.company-files-widget .file-progress svg {
    display: block;
    width: 20px;
    height: 25px;
    margin: 0 auto;
}

.company-files-widget .file-progress p {
    margin: 0;
}

.company-files-widget .file-progress .progress {
    width: 100%;
}

.company-files-widget .file-progress .file-progress-text {
    width: 100%;
    display: block;
    text-align: right;
    margin: 0;
}

.company-files-widget .file-progress .progress .progress-bar {
    background-color: #27dc44;
}

.company-files-widget .progress .progress-bar.complete {
    background-color: #17b8ff;
}

.company-files-widget .add-item-upload-section h3 {
    position: relative;
    z-index: -1;
    margin-bottom: 10px;
}

.company-files-widget .add-item-upload-section .select-files-button {
    font-size: 14px;
}

.company-files-widget .add-item-inner-container .file-upload {
    display: none;
}

.company-files-widget .add-item-container .btn-container {
    text-align: right;
    width: 100%;
    background-color: #f7f7f8;
    padding: 10px;
    border: 1px solid #cac6cb;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
}

.company-files-widget .uploaded-file {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 250px;
}

.company-files-widget .uploaded-file .uploaded-file-details {
    display: flex;
    gap: 10px;
}

.company-files-widget .uploaded-file-details p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-files-widget .uploaded-file-metrics {
    width: 335px;
    margin-left: 10px;
}

.company-files-widget .uploaded-file-metrics .file-too-large {
    padding: 20px 0;
}

.company-files-widget .add-item-upload-section .svg-container {
    position: relative;
    z-index: -1;
    margin-bottom: 10px;
    margin-right: 0;
}

.company-files-widget .add-item-upload-section .svg-container svg {
    width: 103px;
}

.company-files-widget .divider {
    position: relative;
    margin: 20px 0 30px;
    border-bottom: solid 2px #e9e9ef;
}

.company-files-widget .divider .text {
    position: absolute;
    padding: 5px 80px;
    background-color: #fff;
    left: calc(50% - 91px);
    top: calc(50% - 15px);
}

.company-files-widget .bg-wrapper {
    position: relative;
    z-index: 0;
    background-color: #e9e9ef;
    border-radius: 10px;
}

.company-files-widget .upload-link-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.company-files-widget .upload-link-container .app-input {
    width: 455px;
}

.company-files-widget .preview-image-container {
    width: 100%;
    height: 268px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
}

.company-files-widget .preview-image-wrapper {
    width: 100%;
    height: 268px;
    margin-bottom: 15px;
}

.company-files-widget .edit-name {
    padding: 25px;
}

.company-files-widget .edit-name .app-file-icon {
    width: 40%;
    margin: 0 auto;
}

.company-files-widget .preview-image-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}

.company-files-widget .preview-image-wrapper .svg-container {
    margin: 0;
    text-align: center;
    width: 100%;
}

.company-files-widget .preview-image-wrapper .svg-container svg {
    width: 130px;
}
</style>
